import { DownOutlined, MenuOutlined } from '@ant-design/icons';
import { Button, Dropdown, Grid } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FARM_USER_INVITE_UI_ENABLED } from 'farmInviteAccess';
import { TFunction } from 'types';

const COMPANIES_AREA_PREFIX = '/companies';

const isCompaniesArea = (pathname: string) =>
  pathname === COMPANIES_AREA_PREFIX || pathname.startsWith(`${COMPANIES_AREA_PREFIX}/`);

const SECTION_NAVIGATION_ITEMS = [
  { path: '/app-selection', labelKey: 'SectionNavigationAppSelection' },
  { path: '/farm-details', labelKey: 'SectionNavigationFarmDetails' },
  { path: COMPANIES_AREA_PREFIX, labelKey: 'SectionNavigationCompaniesAndFarms' },
  ...(FARM_USER_INVITE_UI_ENABLED
    ? [{ path: '/users' as const, labelKey: 'SectionNavigationUsers' as const }]
    : []),
] as const;

export const SECTION_NAVIGATION_PATHS: readonly string[] = SECTION_NAVIGATION_ITEMS.map((item) => item.path);

export const isSectionNavigationRoute = (pathname: string) =>
  SECTION_NAVIGATION_ITEMS.some(
    (item) =>
      pathname === item.path
      || (item.path === COMPANIES_AREA_PREFIX && isCompaniesArea(pathname)),
  );

export function SectionNavigation({ compact = false }: { compact?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t }: TFunction = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const currentItem =
    SECTION_NAVIGATION_ITEMS.find((item) =>
      item.path === location.pathname
      || (item.path === COMPANIES_AREA_PREFIX && isCompaniesArea(location.pathname)))
    ?? SECTION_NAVIGATION_ITEMS[0];
  const menuItems: MenuProps['items'] = [
    ...SECTION_NAVIGATION_ITEMS.map((item) => ({
      key: item.path,
      label: (
        <span className="section-navigation-menu-label">
          <span>{t(item.labelKey)}</span>
        </span>
      ),
      className: 'section-navigation-menu-item',
    })),
    { type: 'divider' },
    {
      key: '/exit',
      label: <span className="section-navigation-menu-label">{t('logOut')}</span>,
      className: 'section-navigation-menu-item section-navigation-menu-item-logout',
      danger: true,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const nextPath = String(key);

    if (nextPath === location.pathname) {
      return;
    }

    navigate(nextPath);
  };

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="section-navigation-dropdown"
      menu={{
        className: 'section-navigation-menu',
        items: menuItems,
        onClick: handleMenuClick,
        selectable: true,
        selectedKeys: [location.pathname],
      }}
    >
      <Button
        aria-label={t('SectionNavigationMenu')}
        className={`section-navigation-trigger${compact ? ' section-navigation-trigger--compact' : ''}`}
        size="large"
        style={{
          width: compact ? 'auto' : isMobile ? '100%' : 'auto',
          minWidth: compact ? undefined : isMobile ? undefined : 240,
          height: compact ? (isMobile ? 46 : 48) : isMobile ? 64 : 60,
          padding: compact ? '0 14px' : '0 16px',
          justifyContent: compact ? 'center' : 'space-between',
          gap: compact ? 10 : undefined,
        }}
      >
        {compact ? (
          <span className="section-navigation-trigger-compact-layout">
            <span className="section-navigation-trigger-compact-content">
              <span className="section-navigation-trigger-icon">
                <MenuOutlined />
              </span>
              <span className="section-navigation-trigger-compact-label-row">
                <span className="section-navigation-trigger-compact-label">{t('SectionNavigationMenu')}</span>
              </span>
            </span>
            <DownOutlined style={{ fontSize: 12, color: '#6b7280' }} />
          </span>
        ) : (
          <>
            <span className="section-navigation-trigger-content">
              <span className="section-navigation-trigger-icon">
                <MenuOutlined />
              </span>
              <span className="section-navigation-trigger-text">
                <span className="section-navigation-trigger-kicker-row">
                  <span className="section-navigation-trigger-kicker">{t('SectionNavigationMenu')}</span>
                </span>
                <span className="section-navigation-trigger-label">{t(currentItem.labelKey)}</span>
              </span>
            </span>
            <DownOutlined style={{ fontSize: 12, color: '#6b7280' }} />
          </>
        )}
      </Button>
    </Dropdown>
  );
}
